import { API_BASE } from '../../config/apiBase';
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Separator } from '../../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  ArrowLeft,
  DollarSign,
  CheckCircle,
  AlertTriangle,
  Calculator,
  TrendingUp,
  Plus,
  Minus,
  Download,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const API = API_BASE;

// BASELINE FACTOR RATES - Inspired Funding Official Table
const FACTOR_RATES = {
  56: { 1: 1.375, 2: 1.475, 3: 1.575, 4: 1.675, 5: 1.725 },
  78: { 1: 1.525, 2: 1.575, 3: 1.625, 4: 1.725, 5: 1.775 },
  112: { 1: 1.725, 2: 1.795, 3: 1.865, 4: 1.935, 5: 2.005 },
  156: { 1: 1.875, 2: 1.945, 3: 2.015, 4: 2.085, 5: 2.155 }
};

const TERM_OPTIONS = [
  { weeks: 56, label: '56 Semanas (~13 meses)' },
  { weeks: 78, label: '78 Semanas (~18 meses)' },
  { weeks: 112, label: '112 Semanas (~26 meses)' },
  { weeks: 156, label: '156 Semanas (~36 meses)' }
];

const PricingApp = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('client');
  
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [appraisals, setAppraisals] = useState([]);
  const [creditEvals, setCreditEvals] = useState([]);
  
  const [selectedClient, setSelectedClient] = useState(clientId || undefined);
  const [selectedAppraisal, setSelectedAppraisal] = useState(undefined);
  const [selectedCreditEval, setSelectedCreditEval] = useState(undefined);
  const [tier, setTier] = useState('2');
  const [equipmentPrice, setEquipmentPrice] = useState('');
  const [downPayment, setDownPayment] = useState('');
  const [termWeeks, setTermWeeks] = useState(78);
  const [factorAdjustment, setFactorAdjustment] = useState(0);
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [notes, setNotes] = useState('');
  
  // Closing costs (capitalized)
  const [originationFee, setOriginationFee] = useState('');
  const [documentationFee, setDocumentationFee] = useState('');
  const [gpsFee, setGpsFee] = useState('');
  const [inspectionFee, setInspectionFee] = useState('');
  const [otherFees, setOtherFees] = useState('');

  useEffect(() => {
    fetchInitialData();
    if (id && id !== 'new') {
      fetchDeal();
    } else {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (selectedAppraisal) {
      const appraisal = appraisals.find(a => a.id === selectedAppraisal);
      if (appraisal?.asset?.requested_price) {
        setEquipmentPrice(appraisal.asset.requested_price.toString());
      }
    }
  }, [selectedAppraisal, appraisals]);

  useEffect(() => {
    if (selectedCreditEval) {
      const eval_ = creditEvals.find(e => e.id === selectedCreditEval);
      if (eval_?.assigned_tier) {
        setTier(eval_.assigned_tier.toString());
      }
    }
  }, [selectedCreditEval, creditEvals]);

  const fetchInitialData = async () => {
    try {
      const [clientsRes, appraisalsRes, evalsRes] = await Promise.all([
        axios.get(`${API}/clients`),
        axios.get(`${API}/appraisals`),
        axios.get(`${API}/credit-evaluations`)
      ]);
      setClients(clientsRes.data);
      setAppraisals(appraisalsRes.data);
      setCreditEvals(evalsRes.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchDeal = async () => {
    try {
      const response = await axios.get(`${API}/deal-structures/${id}`);
      setDeal(response.data);
      setSelectedClient(response.data.client_id || undefined);
      setTier(response.data.tier?.toString() || '2');
      setEquipmentPrice(response.data.equipment_price?.toString() || '');
      setDownPayment(response.data.down_payment?.toString() || '');
      setTermWeeks(response.data.term_weeks || 78);
      setFactorAdjustment(response.data.factor_adjustment || 0);
      setAdjustmentReason(response.data.adjustment_reason || '');
      setOriginationFee(response.data.origination_fee?.toString() || '');
      setDocumentationFee(response.data.documentation_fee?.toString() || '');
      setGpsFee(response.data.gps_fee?.toString() || '');
      setInspectionFee(response.data.inspection_fee?.toString() || '');
      setOtherFees(response.data.other_fees?.toString() || '');
      setNotes(response.data.notes || '');
    } catch (error) {
      toast.error('Error al cargar el deal');
      navigate('/apps/pricing');
    } finally {
      setLoading(false);
    }
  };

  const calculateMetrics = () => {
    const price = parseFloat(equipmentPrice) || 0;
    const down = parseFloat(downPayment) || 0;
    const tierNum = parseInt(tier) || 2;
    
    // Calculate closing costs
    const closingCosts = 
      (parseFloat(originationFee) || 0) +
      (parseFloat(documentationFee) || 0) +
      (parseFloat(gpsFee) || 0) +
      (parseFloat(inspectionFee) || 0) +
      (parseFloat(otherFees) || 0);
    
    // Amount financed (asset only)
    const amountFinancedAsset = price - down;
    
    // Base financiable final = Amount financed + closing costs capitalized
    const baseFinanciable = amountFinancedAsset + closingCosts;
    
    // Get base factor from table
    const baseFactor = FACTOR_RATES[termWeeks]?.[tierNum] || 1.575;
    
    // Final factor with adjustment
    const finalFactor = baseFactor + factorAdjustment;
    
    // Total to pay
    const totalToPay = baseFinanciable * finalFactor;
    
    // Weekly payment
    const weeklyPayment = totalToPay / termWeeks;
    
    // LTV and Down %
    const downPct = price > 0 ? (down / price * 100) : 0;
    const ltv = price > 0 ? (amountFinancedAsset / price * 100) : 0;
    
    // Warnings
    const warnings = [];
    if (ltv > 85) {
      warnings.push(`LTV (${ltv.toFixed(1)}%) es muy alto. Considere mayor enganche.`);
    }
    if (downPct < 10) {
      warnings.push(`Enganche (${downPct.toFixed(1)}%) es muy bajo para este tier.`);
    }
    if (factorAdjustment < 0 && !adjustmentReason) {
      warnings.push('Se requiere justificación para reducción de factor.');
    }
    
    return {
      price,
      down,
      downPct,
      ltv,
      closingCosts,
      amountFinancedAsset,
      baseFinanciable,
      baseFactor,
      factorAdjustment,
      finalFactor,
      totalToPay,
      weeklyPayment,
      termWeeks,
      warnings,
      isValid: warnings.length === 0 && price > 0 && down > 0
    };
  };

  const metrics = calculateMetrics();

  const handleCreate = async () => {
    if (!tier || !equipmentPrice || !downPayment) {
      toast.error('Tier, precio y enganche son requeridos');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/deal-structures`, {
        client_id: selectedClient || null,
        appraisal_id: selectedAppraisal || null,
        credit_evaluation_id: selectedCreditEval || null,
        tier: parseInt(tier),
        equipment_price: parseFloat(equipmentPrice),
        down_payment: parseFloat(downPayment),
        term_weeks: termWeeks,
        base_factor: metrics.baseFactor,
        factor_adjustment: factorAdjustment,
        final_factor: metrics.finalFactor,
        adjustment_reason: adjustmentReason,
        origination_fee: parseFloat(originationFee) || 0,
        documentation_fee: parseFloat(documentationFee) || 0,
        gps_fee: parseFloat(gpsFee) || 0,
        inspection_fee: parseFloat(inspectionFee) || 0,
        other_fees: parseFloat(otherFees) || 0,
        closing_costs_total: metrics.closingCosts,
        base_financiable: metrics.baseFinanciable,
        total_to_pay: metrics.totalToPay,
        weekly_payment: metrics.weeklyPayment,
        notes
      });
      toast.success('Estructura de deal creada');
      navigate(`/apps/pricing/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear deal');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/deal-structures/${id}`, {
        client_id: selectedClient || null,
        appraisal_id: selectedAppraisal || null,
        credit_evaluation_id: selectedCreditEval || null,
        tier: parseInt(tier),
        equipment_price: parseFloat(equipmentPrice),
        down_payment: parseFloat(downPayment),
        term_weeks: termWeeks,
        base_factor: metrics.baseFactor,
        factor_adjustment: factorAdjustment,
        final_factor: metrics.finalFactor,
        adjustment_reason: adjustmentReason,
        origination_fee: parseFloat(originationFee) || 0,
        documentation_fee: parseFloat(documentationFee) || 0,
        gps_fee: parseFloat(gpsFee) || 0,
        inspection_fee: parseFloat(inspectionFee) || 0,
        other_fees: parseFloat(otherFees) || 0,
        closing_costs_total: metrics.closingCosts,
        base_financiable: metrics.baseFinanciable,
        total_to_pay: metrics.totalToPay,
        weekly_payment: metrics.weeklyPayment,
        notes
      });
      toast.success('Deal actualizado');
      fetchDeal();
    } catch (error) {
      toast.error('Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pricing-app-page">
      <Button variant="ghost" onClick={() => navigate('/apps/pricing')}>
        <ArrowLeft className="w-4 h-4 mr-2" />
        Volver a Pricing
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Panel */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-amber-600" />
              {id && id !== 'new' ? 'Editar Deal Structure' : 'Nuevo Deal Structure'}
            </CardTitle>
            <CardDescription>Motor de Pricing - Factor Rates por Tier y Plazo</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Import from existing data */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cliente (Opcional)</Label>
                <Select value={selectedClient} onValueChange={setSelectedClient}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin cliente</SelectItem>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Importar de Asignación de Riesgo</Label>
                <Select value={selectedCreditEval} onValueChange={setSelectedCreditEval}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ninguna</SelectItem>
                    {creditEvals.filter(e => e.assigned_tier).map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.client_name || 'Sin cliente'} - Tier {e.assigned_tier}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator />

            {/* Core inputs */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tier *</Label>
                <Select value={tier} onValueChange={setTier}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar tier..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Tier 1 - Premium</SelectItem>
                    <SelectItem value="2">Tier 2 - Standard</SelectItem>
                    <SelectItem value="3">Tier 3 - Subprime</SelectItem>
                    <SelectItem value="4">Tier 4 - Deep Subprime</SelectItem>
                    <SelectItem value="5">Tier 5 - High Risk</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Plazo *</Label>
                <Select value={termWeeks.toString()} onValueChange={(v) => setTermWeeks(parseInt(v))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar plazo..." />
                  </SelectTrigger>
                  <SelectContent>
                    {TERM_OPTIONS.map((opt) => (
                      <SelectItem key={opt.weeks} value={opt.weeks.toString()}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Precio del Activo ($) *</Label>
                <Input
                  type="number"
                  value={equipmentPrice}
                  onChange={(e) => setEquipmentPrice(e.target.value)}
                  placeholder="60000"
                />
              </div>
              <div className="space-y-2">
                <Label>Down Payment ($) *</Label>
                <Input
                  type="number"
                  value={downPayment}
                  onChange={(e) => setDownPayment(e.target.value)}
                  placeholder="10000"
                />
                {equipmentPrice && downPayment && (
                  <p className="text-sm text-muted-foreground">
                    {((parseFloat(downPayment) / parseFloat(equipmentPrice)) * 100).toFixed(1)}% del precio
                  </p>
                )}
              </div>
            </div>

            {/* Closing Costs Section */}
            <div className="p-4 rounded-lg border bg-muted/30">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Gastos de Cierre (Capitalizados)
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Origination Fee</Label>
                  <Input
                    type="number"
                    value={originationFee}
                    onChange={(e) => setOriginationFee(e.target.value)}
                    placeholder="0"
                    className="h-8"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Documentation Fee</Label>
                  <Input
                    type="number"
                    value={documentationFee}
                    onChange={(e) => setDocumentationFee(e.target.value)}
                    placeholder="0"
                    className="h-8"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">GPS / Telematics</Label>
                  <Input
                    type="number"
                    value={gpsFee}
                    onChange={(e) => setGpsFee(e.target.value)}
                    placeholder="0"
                    className="h-8"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Inspection Fee</Label>
                  <Input
                    type="number"
                    value={inspectionFee}
                    onChange={(e) => setInspectionFee(e.target.value)}
                    placeholder="0"
                    className="h-8"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Otros Fees</Label>
                  <Input
                    type="number"
                    value={otherFees}
                    onChange={(e) => setOtherFees(e.target.value)}
                    placeholder="0"
                    className="h-8"
                  />
                </div>
                <div className="flex items-end">
                  <div className="p-2 rounded bg-amber-100 dark:bg-amber-900/30 w-full text-center">
                    <p className="text-xs text-muted-foreground">Total</p>
                    <p className="font-bold text-amber-700">${metrics.closingCosts.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Factor Adjustment */}
            <div className="p-4 rounded-lg border bg-blue-50 dark:bg-blue-900/20">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Ajuste de Factor
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Factor Base (Tier {tier}, {termWeeks} sem.)</Label>
                  <div className="text-2xl font-bold text-blue-700">{metrics.baseFactor.toFixed(3)}</div>
                </div>
                <div className="space-y-2">
                  <Label>Ajuste (+/-)</Label>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setFactorAdjustment(prev => Math.max(prev - 0.025, -0.1))}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <Input
                      type="number"
                      value={factorAdjustment}
                      onChange={(e) => setFactorAdjustment(parseFloat(e.target.value) || 0)}
                      step="0.025"
                      className="w-24 text-center"
                    />
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setFactorAdjustment(prev => prev + 0.025)}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              {factorAdjustment !== 0 && (
                <div className="mt-3 space-y-2">
                  <Label>Justificación del Ajuste *</Label>
                  <Textarea
                    value={adjustmentReason}
                    onChange={(e) => setAdjustmentReason(e.target.value)}
                    placeholder="Ej: +0.05 por NSFs recientes + DP bajo"
                    rows={2}
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notas sobre el deal..."
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => navigate('/apps/pricing')}>Cancelar</Button>
              <Button onClick={id && id !== 'new' ? handleUpdate : handleCreate} disabled={saving}>
                {saving ? 'Guardando...' : (id && id !== 'new' ? 'Actualizar' : 'Crear Deal')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results Panel */}
        <div className="space-y-6">
          {/* Deal Summary - Inspired Funding Format */}
          <Card className="border-2 border-emerald-200 dark:border-emerald-800">
            <CardHeader className="bg-emerald-50 dark:bg-emerald-900/20">
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="w-5 h-5" />
                  Resumen del Deal
                </CardTitle>
                {id && id !== 'new' && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => window.open(`${API}/deal-structures/${id}/export-pdf`, '_blank')}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Exportar PDF
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Tier Asignado</span>
                  <span className="font-bold">Tier {tier}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Plazo Aprobado</span>
                  <span className="font-bold">{termWeeks} semanas</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Factor Base</span>
                  <span className="font-medium">{metrics.baseFactor.toFixed(3)}</span>
                </div>
                {factorAdjustment !== 0 && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Ajuste Aplicado</span>
                    <span className={cn(
                      'font-medium',
                      factorAdjustment > 0 ? 'text-red-600' : 'text-green-600'
                    )}>
                      {factorAdjustment > 0 ? '+' : ''}{factorAdjustment.toFixed(3)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between py-2 border-b bg-blue-50 dark:bg-blue-900/20 px-2 rounded">
                  <span className="font-medium">Factor Final</span>
                  <span className="font-bold text-blue-700">{metrics.finalFactor.toFixed(3)}</span>
                </div>
                
                <Separator className="my-2" />
                
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Precio del Activo</span>
                  <span className="font-medium">${metrics.price.toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Down Payment</span>
                  <span className="font-medium">-${metrics.down.toLocaleString()} ({metrics.downPct.toFixed(1)}%)</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Gastos de Cierre Capitalizados</span>
                  <span className="font-medium">+${metrics.closingCosts.toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-2 border-b bg-amber-50 dark:bg-amber-900/20 px-2 rounded">
                  <span className="font-medium">Base Financiable</span>
                  <span className="font-bold text-amber-700">${metrics.baseFinanciable.toLocaleString()}</span>
                </div>
                
                <Separator className="my-2" />
                
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Monto Total a Pagar</span>
                  <span className="font-medium">${metrics.totalToPay.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                </div>
              </div>
              
              {/* Weekly Payment - Highlighted */}
              <div className="mt-4 p-4 rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-600 text-white">
                <p className="text-sm opacity-90">Pago Semanal</p>
                <p className="text-3xl font-bold">
                  ${metrics.weeklyPayment.toLocaleString(undefined, {maximumFractionDigits: 2})}
                </p>
                <p className="text-xs opacity-75 mt-1">
                  {termWeeks} pagos semanales
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Factor Rate Reference Table */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Tabla de Factor Rates - Inspired Funding</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b">
                      <th className="py-2 px-2 text-left">Semanas</th>
                      <th className="py-2 px-1 text-center">T1</th>
                      <th className="py-2 px-1 text-center">T2</th>
                      <th className="py-2 px-1 text-center">T3</th>
                      <th className="py-2 px-1 text-center">T4</th>
                      <th className="py-2 px-1 text-center">T5</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(FACTOR_RATES).map(([weeks, tiers]) => (
                      <tr 
                        key={weeks} 
                        className={cn(
                          'border-b',
                          parseInt(weeks) === termWeeks && 'bg-blue-50 dark:bg-blue-900/20'
                        )}
                      >
                        <td className="py-2 px-2 font-medium">{weeks}</td>
                        {[1, 2, 3, 4, 5].map(t => (
                          <td 
                            key={t} 
                            className={cn(
                              'py-2 px-1 text-center',
                              parseInt(weeks) === termWeeks && t === parseInt(tier) && 'font-bold text-blue-700'
                            )}
                          >
                            {tiers[t].toFixed(3)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Warnings */}
          {metrics.warnings.length > 0 && (
            <Card className="border-amber-200 dark:border-amber-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="w-5 h-5" />
                  Advertencias
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {metrics.warnings.map((warning, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-2 rounded bg-amber-50 dark:bg-amber-900/20 text-amber-700 text-sm">
                      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{warning}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Valid Status */}
          {metrics.isValid && (
            <Card className="border-emerald-200 dark:border-emerald-800">
              <CardContent className="p-4 flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
                <div>
                  <p className="font-semibold text-emerald-700 dark:text-emerald-400">Deal Válido</p>
                  <p className="text-sm text-muted-foreground">Pricing consistente con políticas de Inspired Funding</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default PricingApp;
